import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthActionTypes } from '../../../features/auth/types';
import { UserAuthActionTypes } from '../../../features/user/userStore';
import type { User } from '../../../shared/lib/types/Types';
import { Subscription, SubscriptionState, SubscriptionStatus } from './types';
import { fetchSubscription } from './fetch-subscription';
import { syncSubscriptionAfterPayment } from './sync-subscription-after-payment';
import { cancelSubscriptionAutoRenewal } from './cancel-subscription-auto-renewal';

const initialState: SubscriptionState = {
    data: null,
    loading: 'idle',
    error: null,
    currentRequestId: null,
    requestUserId: null,
    paymentSync: {
        result: null,
        loading: 'idle',
        error: null,
        currentRequestId: null,
        requestUserId: null,
    },
    cancellation: {
        loading: 'idle',
        error: null,
        currentRequestId: null,
        requestUserId: null,
    },
};

export const subscriptionSlice = createSlice({
    name: 'subscription',
    initialState,
    selectors: {
        selectSubscription: (state) => state.data,
        selectSubscriptionStatus: (state) => state.data?.subscriptionStatusId ?? null,
        selectSubscriptionPlan: (state) => state.data?.subscriptionPlanId ?? null,
        selectSubscriptionLoading: (state) => state.loading,
        selectSubscriptionError: (state) => state.error,
        selectSubscriptionPaymentSync: (state) => state.paymentSync,
        selectSubscriptionCancellation: (state) => state.cancellation,
        selectIsSubscriptionLoaded: (state) => state.data !== null,
        selectIsTrial: (state) => state.data?.subscriptionStatusId === SubscriptionStatus.TrialActive
            || state.data?.subscriptionStatusId === SubscriptionStatus.TrialExpiring,
        selectIsBasic: (state) => state.data?.subscriptionStatusId === SubscriptionStatus.Basic,
        // Cancellation and payment retries preserve access until Backend changes the status.
        // Unknown data is not Basic; consumers must check selectIsSubscriptionLoaded before gating.
        selectHasProAccess: (state) => state.data !== null && (
            state.data.subscriptionStatusId === SubscriptionStatus.TrialActive
            || state.data.subscriptionStatusId === SubscriptionStatus.TrialExpiring
            || state.data.subscriptionStatusId === SubscriptionStatus.ProActive
            || state.data.subscriptionStatusId === SubscriptionStatus.ProExpiring
            || state.data.subscriptionStatusId === SubscriptionStatus.ProPaymentFailed
            || state.data.subscriptionStatusId === SubscriptionStatus.ProCancelled
        ),
    },
    reducers: {
        subscriptionLoading: (state) => {
            state.loading = 'pending';
            state.error = null;
        },
        subscriptionReceived: (state, action: PayloadAction<Subscription>) => {
            state.data = action.payload;
            state.loading = 'success';
            state.error = null;
            state.currentRequestId = null;
            state.requestUserId = null;
        },
        subscriptionFailed: (state, action: PayloadAction<string>) => {
            state.loading = 'error';
            state.error = action.payload;
            state.currentRequestId = null;
            state.requestUserId = null;
        },
        resetSubscription: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(cancelSubscriptionAutoRenewal.pending, (state, action) => {
                state.cancellation = {
                    loading: 'pending',
                    error: null,
                    currentRequestId: action.meta.requestId,
                    requestUserId: action.meta.userId,
                };
            })
            .addCase(cancelSubscriptionAutoRenewal.fulfilled, (state, action) => {
                if (state.cancellation.currentRequestId !== action.meta.requestId) return;
                state.data = action.payload;
                state.loading = 'success';
                state.error = null;
                // All GETs started before this confirmed mutation are obsolete,
                // including focus refreshes started while cancellation was pending.
                state.currentRequestId = null;
                state.requestUserId = null;
                state.cancellation = {
                    loading: 'success', error: null, currentRequestId: null, requestUserId: null,
                };
            })
            .addCase(cancelSubscriptionAutoRenewal.rejected, (state, action) => {
                if (state.cancellation.currentRequestId !== action.meta.requestId) return;
                state.cancellation = {
                    loading: action.meta.aborted ? 'idle' : 'error',
                    error: action.meta.aborted ? null : action.payload ?? 'Не удалось отменить автопродление.',
                    currentRequestId: null,
                    requestUserId: null,
                };
            })
            .addCase(syncSubscriptionAfterPayment.pending, (state, action) => {
                state.paymentSync = {
                    result: null,
                    loading: 'pending',
                    error: null,
                    currentRequestId: action.meta.requestId,
                    requestUserId: action.meta.userId,
                };
            })
            .addCase(syncSubscriptionAfterPayment.fulfilled, (state, action) => {
                if (state.paymentSync.currentRequestId !== action.meta.requestId) return;
                state.paymentSync.result = action.payload;
                state.paymentSync.loading = 'success';
                state.paymentSync.currentRequestId = null;
                state.paymentSync.requestUserId = null;
            })
            .addCase(syncSubscriptionAfterPayment.rejected, (state, action) => {
                if (state.paymentSync.currentRequestId !== action.meta.requestId) return;
                state.paymentSync.loading = action.meta.aborted ? 'idle' : 'error';
                state.paymentSync.error = action.meta.aborted ? null : action.payload ?? 'Не удалось обновить подписку после оплаты.';
                state.paymentSync.currentRequestId = null;
                state.paymentSync.requestUserId = null;
            })
            .addCase(fetchSubscription.pending, (state, action) => {
                if (state.data && state.data.userId !== action.meta.userId) {
                    state.data = null;
                }
                state.loading = 'pending';
                state.error = null;
                state.currentRequestId = action.meta.requestId;
                state.requestUserId = action.meta.userId;
            })
            .addCase(fetchSubscription.fulfilled, (state, action) => {
                if (state.currentRequestId !== action.meta.requestId) return;
                state.data = action.payload;
                state.loading = 'success';
                state.error = null;
                state.currentRequestId = null;
                state.requestUserId = null;
            })
            .addCase(fetchSubscription.rejected, (state, action) => {
                if (state.currentRequestId !== action.meta.requestId) return;
                state.loading = action.meta.aborted ? (state.data ? 'success' : 'idle') : 'error';
                state.error = action.meta.aborted ? null : action.payload ?? 'Не удалось загрузить подписку. Повторите попытку.';
                state.currentRequestId = null;
                state.requestUserId = null;
            })
            .addCase(UserAuthActionTypes.FETCH_DATA_USER_ACCOUNT_LOGOUT, () => initialState)
            .addCase(UserAuthActionTypes.FETCH_DATA_RESPONSE_USER_ACCOUNT_ERR, () => initialState)
            .addCase(AuthActionTypes.LOGOUT, () => initialState)
            .addCase(UserAuthActionTypes.FETCH_DATA_RESPONSE_USER_ACCOUNT,
                (state, action: PayloadAction<User, UserAuthActionTypes.FETCH_DATA_RESPONSE_USER_ACCOUNT>) => {
                    if ((state.data && state.data.userId !== action.payload.userId)
                        || (state.requestUserId && state.requestUserId !== action.payload.userId)
                        || (state.paymentSync.requestUserId && state.paymentSync.requestUserId !== action.payload.userId)
                        || (state.cancellation.requestUserId && state.cancellation.requestUserId !== action.payload.userId)) {
                        return initialState;
                    }
                });
    },
});

export const {
    subscriptionLoading,
    subscriptionReceived,
    subscriptionFailed,
    resetSubscription,
} = subscriptionSlice.actions;

export const {
    selectSubscription,
    selectSubscriptionStatus,
    selectSubscriptionPlan,
    selectSubscriptionLoading,
    selectSubscriptionError,
    selectSubscriptionPaymentSync,
    selectSubscriptionCancellation,
    selectIsSubscriptionLoaded,
    selectIsTrial,
    selectIsBasic,
    selectHasProAccess,
} = subscriptionSlice.selectors;
